import React, { useEffect, useMemo, useState } from "react";
import {
  AlertOutlined,
  AppstoreOutlined,
  ArrowRightOutlined,
  BarChartOutlined,
  CheckCircleFilled,
  DollarCircleOutlined,
  PercentageOutlined,
  PieChartOutlined,
  ShoppingCartOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { Button } from "antd";

import { getDailyRecapRows } from "../utils/dailyRecapDisplay";
import DailyRecapStockOutages from "./DailyRecapStockOutages";

type DailyRecapData = Record<string, unknown>;

interface DailyRecapBriefingSummaryProps {
  data?: DailyRecapData | null;
  message?: string | null;
  reportUrl: string;
  onOpenDestination: (url: string) => void;
}

interface AttentionItem {
  key: string;
  label: string;
  value: string;
  tone: "warning" | "danger";
  icon: React.ReactNode;
}

interface DailyRecapAnomaly {
  type: string;
  severity: string;
  title: string;
  impactLabel: string;
  actionLabel: string;
  actionPath: string | null;
}

const RESTAURANT_DATA_KEYS = [
  "prepared_items_count",
  "production_cost",
  "remaining_prepared_items_count",
  "unsold_value_rate",
  "actual_margin",
  "dishes_to_watch",
  "open_anomalies",
];

const hasOwn = (data: DailyRecapData, key: string) =>
  Object.prototype.hasOwnProperty.call(data, key);

const asNumber = (value: unknown) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const asText = (value: unknown) =>
  typeof value === "string" ? value.trim() : "";

const asRecordArray = (value: unknown): DailyRecapData[] =>
  Array.isArray(value)
    ? value.filter(
        (item): item is DailyRecapData =>
          Boolean(item) && typeof item === "object" && !Array.isArray(item)
      )
    : [];

const formatQuantity = (value: unknown) =>
  asNumber(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const normalizeInternalPath = (value: unknown): string | null => {
  const path = asText(value);
  return /^\/(?!\/)/.test(path) ? path : null;
};

const normalizeAnomaly = (value: DailyRecapData): DailyRecapAnomaly => ({
  type: asText(value.type),
  severity: asText(value.severity),
  title: asText(value.title) || "Point d’attention à traiter",
  impactLabel: asText(value.impact_label),
  actionLabel: asText(value.action_label),
  actionPath: normalizeInternalPath(value.action_path),
});

export const getDailyRecapProfileLabel = (data: DailyRecapData = {}) =>
  RESTAURANT_DATA_KEYS.some((key) => hasOwn(data, key))
    ? "Restaurant / Production"
    : "Activité commerciale";

const getHighlightIcon = (key: string) => {
  switch (key) {
    case "total_sales":
      return <BarChartOutlined aria-hidden="true" />;
    case "total_payments_in":
      return <DollarCircleOutlined aria-hidden="true" />;
    case "actual_margin":
      return <PieChartOutlined aria-hidden="true" />;
    case "net_cash_flow":
      return <WalletOutlined aria-hidden="true" />;
    case "prepared_items_count":
      return <AppstoreOutlined aria-hidden="true" />;
    default:
      return <ShoppingCartOutlined aria-hidden="true" />;
  }
};

const DailyRecapBriefingSummary: React.FC<
  DailyRecapBriefingSummaryProps
> = ({ data = null, message, reportUrl, onOpenDestination }) => {
  const safeData = useMemo(() => data || {}, [data]);
  const rows = useMemo(() => getDailyRecapRows(safeData), [safeData]);
  const rowByKey = useMemo(
    () => new Map(rows.map((row) => [row.key, row])),
    [rows]
  );
  const anomalies = useMemo(
    () => asRecordArray(safeData.open_anomalies).map(normalizeAnomaly),
    [safeData]
  );
  const primaryAnomaly = anomalies[0] || null;
  const [stockOutagesOpen, setStockOutagesOpen] = useState(false);
  useEffect(() => setStockOutagesOpen(false), [safeData]);
  const isStockOutagePriority =
    primaryAnomaly?.type === "stock_risk" &&
    /rupture/i.test(primaryAnomaly.title);
  const stockOutagesSnapshot = safeData.stock_outages_snapshot;
  const hasStockOutagesSnapshot =
    Boolean(stockOutagesSnapshot) &&
    typeof stockOutagesSnapshot === "object" &&
    !Array.isArray(stockOutagesSnapshot) &&
    Array.isArray((stockOutagesSnapshot as DailyRecapData).items);
  const primaryIsCritical = primaryAnomaly?.severity === "critical";
  const openAnomalyCount = Math.max(
    anomalies.length,
    Math.round(asNumber(safeData.open_anomaly_count))
  );
  const isRestaurantProfile =
    getDailyRecapProfileLabel(safeData) === "Restaurant / Production";

  const otherAttentionItems = useMemo(() => {
    const items: AttentionItem[] = anomalies.slice(1).map((anomaly, index) => ({
      key: `anomaly-${index}`,
      label: anomaly.title,
      value: anomaly.impactLabel || "À vérifier",
      tone: anomaly.severity === "critical" ? "danger" : "warning",
      icon: <AlertOutlined aria-hidden="true" />,
    }));

    const unpaidSales = rowByKey.get("unpaid_sales_amount");
    if (unpaidSales && asNumber(safeData.unpaid_sales_amount) > 0) {
      items.push({
        key: "unpaid-sales",
        label: unpaidSales.label,
        value: unpaidSales.value,
        tone: "warning",
        icon: <DollarCircleOutlined aria-hidden="true" />,
      });
    }

    const unsoldRate = rowByKey.get("unsold_value_rate");
    if (unsoldRate && asNumber(safeData.unsold_value_rate) > 0) {
      items.push({
        key: "unsold-rate",
        label: unsoldRate.label,
        value: unsoldRate.value,
        tone: "warning",
        icon: <PercentageOutlined aria-hidden="true" />,
      });
    }

    return items.slice(0, 3);
  }, [anomalies, rowByKey, safeData]);

  const dishesToWatch = useMemo(
    () => asRecordArray(safeData.dishes_to_watch).slice(0, 3),
    [safeData]
  );

  const highlights = useMemo(() => {
    const performanceKey = hasOwn(safeData, "actual_margin")
      ? "actual_margin"
      : "net_cash_flow";
    const activityKey = hasOwn(safeData, "prepared_items_count")
      ? "prepared_items_count"
      : hasOwn(safeData, "sales_count")
        ? "sales_count"
        : "items_sold";
    const preferredKeys = [
      "total_sales",
      "total_payments_in",
      performanceKey,
      activityKey,
    ];

    return preferredKeys.flatMap((key, index) => {
      const row = rowByKey.get(key);
      return row
        ? [
            {
              ...row,
              icon: getHighlightIcon(key),
              tone: ["blue", "green", "violet", "amber"][index],
            },
          ]
        : [];
    });
  }, [rowByKey, safeData]);

  if (rows.length === 0) {
    return (
      <section className="daily-recap-briefing-summary__fallback">
        <strong>Résumé de la journée</strong>
        <p>{message || "Aucune opération enregistrée pour cette journée."}</p>
      </section>
    );
  }

  const primaryActionPath = primaryAnomaly?.actionPath || reportUrl;
  const primaryActionLabel = isStockOutagePriority
    ? hasStockOutagesSnapshot
      ? stockOutagesOpen
        ? "Masquer les ruptures"
        : "Examiner les ruptures"
      : null
    : primaryAnomaly?.actionLabel || "Voir le bilan détaillé";
  const primaryTitle = primaryAnomaly
    ? primaryAnomaly.type === "stock_risk"
      ? /rupture/i.test(primaryAnomaly.title)
        ? "Ruptures de stock"
        : "Stock sous surveillance"
      : primaryAnomaly.title
    : openAnomalyCount > 0
      ? `${openAnomalyCount} point${openAnomalyCount > 1 ? "s" : ""} d’attention`
      : "Aucune alerte prioritaire détectée";
  const primaryDetail = primaryAnomaly
    ? primaryAnomaly.type === "stock_risk"
      ? isStockOutagePriority && !hasStockOutagesSnapshot
        ? "Des ruptures ont été signalées dans ce bilan."
        : primaryAnomaly.title
      : primaryAnomaly.impactLabel
    : "";

  return (
    <div className="daily-recap-briefing-summary">
      <section
        className={`daily-recap-briefing-summary__priority${
          primaryAnomaly || openAnomalyCount > 0
            ? primaryIsCritical
              ? " daily-recap-briefing-summary__priority--danger"
              : " daily-recap-briefing-summary__priority--alert"
            : " daily-recap-briefing-summary__priority--clear"
        }`}
        aria-label="Priorité principale du jour"
      >
        <span className="daily-recap-briefing-summary__priority-icon" aria-hidden="true">
          {primaryAnomaly || openAnomalyCount > 0 ? (
            <AlertOutlined />
          ) : (
            <CheckCircleFilled />
          )}
        </span>
        <div className="daily-recap-briefing-summary__priority-copy">
          <strong>{primaryTitle}</strong>
          {primaryDetail ? <span>{primaryDetail}</span> : null}
          {isStockOutagePriority && !hasStockOutagesSnapshot ? (
            <span>La liste des produits n’a pas été enregistrée avec ce bilan.</span>
          ) : null}
          <small>
            {primaryAnomaly || openAnomalyCount > 0
              ? "Priorité principale du jour"
              : "Les contrôles disponibles ne signalent aucun blocage."}
          </small>
        </div>
        {(primaryAnomaly || openAnomalyCount > 0) && primaryActionLabel ? (
          <Button
            type="link"
            className="daily-recap-briefing-summary__priority-action"
            icon={<ArrowRightOutlined />}
            iconPosition="end"
            onClick={() =>
              isStockOutagePriority
                ? setStockOutagesOpen((previous) => !previous)
                : onOpenDestination(primaryActionPath)
            }
          >
            {primaryActionLabel}
          </Button>
        ) : null}
      </section>

      {isStockOutagePriority && hasStockOutagesSnapshot && stockOutagesOpen ? (
        <DailyRecapStockOutages
          snapshot={stockOutagesSnapshot as DailyRecapData}
          warehouseName={safeData.warehouse_name}
          recapDate={safeData.recap_date}
          onClose={() => setStockOutagesOpen(false)}
        />
      ) : null}

      <div
        className={`daily-recap-briefing-summary__middle${
          isRestaurantProfile ? "" : " daily-recap-briefing-summary__middle--single"
        }`}
      >
        <section className="daily-recap-briefing-summary__panel" aria-labelledby="daily-recap-other-points">
          <header>
            <AlertOutlined aria-hidden="true" />
            <h3 id="daily-recap-other-points">Autres points à suivre</h3>
          </header>
          {otherAttentionItems.length > 0 ? (
            <div className="daily-recap-briefing-summary__attention-list">
              {otherAttentionItems.map((item) => (
                <div
                  key={item.key}
                  className={`daily-recap-briefing-summary__attention daily-recap-briefing-summary__attention--${item.tone}`}
                >
                  <span aria-hidden="true">{item.icon}</span>
                  <strong>{item.label}</strong>
                  <b>{item.value}</b>
                </div>
              ))}
            </div>
          ) : (
            <div className="daily-recap-briefing-summary__empty-state">
              <CheckCircleFilled aria-hidden="true" />
              <span>Aucun autre signal prioritaire.</span>
            </div>
          )}
        </section>

        {isRestaurantProfile ? (
          <section
            className="daily-recap-briefing-summary__panel daily-recap-briefing-summary__panel--dishes"
            aria-labelledby="daily-recap-dishes"
          >
            <header>
              <AppstoreOutlined aria-hidden="true" />
              <h3 id="daily-recap-dishes">Plats à surveiller</h3>
            </header>
            {dishesToWatch.length > 0 ? (
              <ol className="daily-recap-briefing-summary__dish-list">
                {dishesToWatch.map((dish, index) => (
                  <li key={`${asText(dish.product_name) || "plat"}-${index}`}>
                    <span>{index + 1}</span>
                    <strong>{asText(dish.product_name) || "Plat"}</strong>
                    <b>
                      {formatQuantity(dish.available_output_quantity)}{" "}
                      {asText(dish.unit_short_name) || "unité(s)"} restant(s)
                    </b>
                  </li>
                ))}
              </ol>
            ) : (
              <div className="daily-recap-briefing-summary__empty-state">
                <CheckCircleFilled aria-hidden="true" />
                <span>Aucun plat ne nécessite une surveillance particulière.</span>
              </div>
            )}
          </section>
        ) : null}
      </div>

      {highlights.length > 0 ? (
        <section
          className="daily-recap-briefing-summary__highlights"
          aria-label="Indicateurs clés"
        >
          {highlights.map((highlight) => (
            <article
              key={highlight.key}
              className={`daily-recap-briefing-summary__highlight daily-recap-briefing-summary__highlight--${highlight.tone}`}
            >
              <span className="daily-recap-briefing-summary__highlight-icon">
                {highlight.icon}
              </span>
              <span>
                <small>{highlight.label}</small>
                <strong>{highlight.value}</strong>
              </span>
            </article>
          ))}
        </section>
      ) : null}
    </div>
  );
};

export default DailyRecapBriefingSummary;
